import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { formatDateTime } from "../lib/nutrition";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, PencilSimple, Trash, ShieldCheck, User as UserIcon } from "@phosphor-icons/react";

const empty = { username: "", full_name: "", password: "", is_admin: false };

export default function Admin() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [editor, setEditor] = useState(null); // {mode:'create'|'edit', data:{...}, id?:string}
  const [delTarget, setDelTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch {
      toast.error("Konnte Benutzerliste nicht laden");
    }
  };

  useEffect(() => { if (me?.is_admin) load(); }, [me]);

  if (!me) return null;
  if (!me.is_admin) return <Navigate to="/" replace />;

  const openCreate = () => setEditor({ mode: "create", data: { ...empty } });
  const openEdit = (u) => setEditor({
    mode: "edit", id: u.id,
    data: { username: u.username, full_name: u.full_name, password: "", is_admin: !!u.is_admin },
  });

  const submit = async () => {
    if (!editor) return;
    const d = editor.data;
    if (!d.username.trim() || !d.full_name.trim()) {
      toast.error("Benutzername und Name sind Pflicht");
      return;
    }
    setBusy(true);
    try {
      if (editor.mode === "create") {
        if (!d.password || d.password.length < 6) {
          toast.error("Passwort muss min. 6 Zeichen haben");
          setBusy(false);
          return;
        }
        await api.post("/admin/users", {
          username: d.username, full_name: d.full_name,
          password: d.password, is_admin: d.is_admin,
        });
        toast.success("Benutzer angelegt");
      } else {
        await api.put(`/admin/users/${editor.id}`, {
          username: d.username, full_name: d.full_name,
          password: d.password || undefined,
          is_admin: d.is_admin,
        });
        toast.success("Benutzer aktualisiert");
      }
      setEditor(null);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Speichern fehlgeschlagen";
      toast.error(typeof msg === "string" ? msg : "Speichern fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!delTarget) return;
    try {
      await api.delete(`/admin/users/${delTarget.id}`);
      toast.success("Benutzer gelöscht");
      setDelTarget(null);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Löschen fehlgeschlagen";
      toast.error(typeof msg === "string" ? msg : "Löschen fehlgeschlagen");
    }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Administration</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Benutzer verwalten</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Benutzer anlegen, Namen oder Passwort ändern, Admin-Rechte vergeben.
          </p>
        </div>
        <Button onClick={openCreate} className="bg-[#002FA7] hover:bg-[#00248a] rounded-sm" data-testid="admin-new-user">
          <Plus size={14} className="mr-2" /> Neuer Benutzer
        </Button>
      </header>

      <div className="border border-zinc-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-500 bg-zinc-50">
              <th className="px-5 py-3 font-medium">Name</th>
              <th className="px-5 py-3 font-medium">Benutzername</th>
              <th className="px-5 py-3 font-medium">Rolle</th>
              <th className="px-5 py-3 font-medium">Angelegt</th>
              <th className="px-5 py-3 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-zinc-100 hover:bg-zinc-50"
                data-testid={`admin-user-row-${u.username}`}>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-zinc-200 flex items-center justify-center text-[10px] font-bold text-zinc-600">
                      {u.full_name?.split(" ").map((s) => s[0]).join("").slice(0, 2)}
                    </div>
                    <span className="font-medium">{u.full_name}</span>
                  </div>
                </td>
                <td className="px-5 py-3 text-zinc-600 font-mono-num">@{u.username}</td>
                <td className="px-5 py-3">
                  {u.is_admin ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm bg-[#002FA7] text-white">
                      <ShieldCheck size={10} weight="fill" /> Admin
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm bg-zinc-100 text-zinc-600 border border-zinc-200">
                      <UserIcon size={10} /> Benutzer
                    </span>
                  )}
                </td>
                <td className="px-5 py-3 text-zinc-500 text-xs font-mono-num">{formatDateTime(u.created_at)}</td>
                <td className="px-5 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                      onClick={() => openEdit(u)} data-testid={`admin-edit-${u.username}`}>
                      <PencilSimple size={14} />
                    </Button>
                    <Button size="sm" variant="ghost"
                      disabled={u.id === me.id}
                      className="h-7 w-7 p-0 text-red-600 hover:bg-red-50 disabled:opacity-40"
                      onClick={() => setDelTarget(u)} data-testid={`admin-delete-${u.username}`}>
                      <Trash size={14} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <Dialog open={!!editor} onOpenChange={(o) => !o && setEditor(null)}>
        <DialogContent className="max-w-md" data-testid="admin-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl tracking-tight">
              {editor?.mode === "create" ? "Neuer Benutzer" : "Benutzer bearbeiten"}
            </DialogTitle>
            <DialogDescription>
              {editor?.mode === "edit"
                ? "Lasse das Passwort leer, um es nicht zu ändern."
                : "Mindestens 6 Zeichen für das Passwort."}
            </DialogDescription>
          </DialogHeader>
          {editor && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-600">Voller Name</Label>
                <Input value={editor.data.full_name}
                  onChange={(e) => setEditor((s) => ({ ...s, data: { ...s.data, full_name: e.target.value } }))}
                  className="mt-1 rounded-sm" data-testid="admin-full-name-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-600">Benutzername</Label>
                <Input value={editor.data.username}
                  onChange={(e) => setEditor((s) => ({ ...s, data: { ...s.data, username: e.target.value.toLowerCase() } }))}
                  className="mt-1 rounded-sm font-mono-num" data-testid="admin-username-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-600">
                  Passwort {editor.mode === "edit" && <span className="normal-case text-zinc-400">(optional)</span>}
                </Label>
                <Input type="password" value={editor.data.password}
                  onChange={(e) => setEditor((s) => ({ ...s, data: { ...s.data, password: e.target.value } }))}
                  placeholder={editor.mode === "edit" ? "Leer lassen = unverändert" : "min. 6 Zeichen"}
                  className="mt-1 rounded-sm" data-testid="admin-password-input" />
              </div>
              <label className="flex items-center gap-2 cursor-pointer pt-2">
                <Checkbox checked={editor.data.is_admin}
                  onCheckedChange={(c) => setEditor((s) => ({ ...s, data: { ...s.data, is_admin: !!c } }))}
                  data-testid="admin-is-admin-checkbox" />
                <span className="text-sm">Administrator-Rechte</span>
              </label>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setEditor(null)}>Abbrechen</Button>
            <Button onClick={submit} disabled={busy}
              className="bg-[#002FA7] hover:bg-[#00248a] rounded-sm" data-testid="admin-save-button">
              {busy ? "Speichern…" : "Speichern"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Benutzer „{delTarget?.full_name}" löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Diese Aktion kann nicht rückgängig gemacht werden. Vom Benutzer erstellte Zutaten
              und Rezepte bleiben erhalten (Audit-Trail), die Anmeldung ist jedoch nicht mehr möglich.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700"
              data-testid="admin-confirm-delete">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
